import { addClass, removeClass, forEach, hasClass } from './utils';

export default class ProductPages {
  constructor(id) {
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleFirstClick = this.handleFirstClick.bind(this);
    this.handleLastClick = this.handleLastClick.bind(this);

    this.id = id;
    this.qLine = document.querySelector(`#id_${this.id}`);
    this.products = this.qLine.querySelectorAll('.js-productItem');

    this.paginationWrapper = this.qLine.querySelector('.paymentCard-pagination');
    this.productsListWrapper = this.qLine.querySelector('.products');
    this.cardQuestionWrapper = this.qLine.querySelector('.jfCard-question');
    this.productsSummaryWrapper = this.qLine.querySelector('.product-summary');
    this.pageWrapper = this.qLine.querySelector('.paymentCard-paginationList');

    this.previousPageButton = this.qLine.querySelector('.paymentCard-paginationLeft.previousPage');
    this.nextPageButton = this.qLine.querySelector('.paymentCard-paginationRight.nextPage');
    this.firstPage = this.qLine.querySelector('.paymentCard-paginationFirst');
    this.lastPage = this.qLine.querySelector('.paymentCard-paginationLast');

    this.onPageChange = null;

    this.isMobile = document.querySelector('.isMobile');
    this.productsPerPageFixed = 0;
    this.maxVisiblePages = 10;
    this.visibleFirstPage = 0;
    this.visibleLastPage = 0;
    this.totalPage = 0;
    this.selectedPage = 1;
  }

  initProductPages() {
    if (!this.paginationWrapper) return;
    this.productsPerPage = this.productsPerPageFixed ? this.productsPerPageFixed : this.getProductsPerPage();
    this.totalPage = Math.ceil(this.products.length / this.productsPerPage);

    forEach(this.products, el => el.show());

    if (this.productsPerPage >= this.products.length) {
      this.paginationWrapper.style.display = 'none';
      forEach(this.products, (p, i) => {
        p.setAttribute('data-page', Math.ceil((i + 1) / this.productsPerPage));
      });
    } else {
      this.paginationWrapper.style.display = 'flex';
      forEach(this.products, (p, i) => {
        p.setAttribute('data-page', Math.ceil((i + 1) / this.productsPerPage));
        if (i + 1 > this.productsPerPage) p.hide();
      });

      this.createPaginationItems();

      this.bindPrevButtonClick();
      this.bindNextButtonClick();
      this.triggerPageChange();
    }
  }

  createPaginationItems() {
    this.removeAllItems();

    const startPage = Math.max(this.selectedPage - Math.floor(this.maxVisiblePages / 2), 1);
    const endPage = Math.min(startPage + this.maxVisiblePages - 1, this.totalPage);

    this.visibleFirstPage = startPage;
    this.visibleLastPage = endPage;

    for (let i = startPage; i <= endPage; i++) {
      const pageItem = this.createPaginationItem(i);

      pageItem.addEventListener('click', () => {
        this.handlePageChange(i);
      });

      this.pageWrapper.appendChild(pageItem);
    }
  }

  createPaginationItem(pageNumber) {
    const pageItem = document.createElement('li');
    pageItem.className = pageNumber === 1 ? 'paymentCard-paginationListItem active' : 'paymentCard-paginationListItem';
    pageItem.setAttribute('data-page', pageNumber);

    const pageLabel = String.prototype.locale ? 'Page'.locale() : 'Page';

    const pageButton = document.createElement('button');
    pageButton.className = 'paymentCard-paginationListButton';
    pageButton.setAttribute('type', 'button');
    pageButton.innerText = pageNumber;
    pageButton.setAttribute('aria-label', `${pageLabel} ${pageNumber}`);

    pageItem.appendChild(pageButton);

    return pageItem;
  }

  removeAllItems() {
    (this.pageWrapper.querySelectorAll('.paymentCard-paginationListItem') || []).forEach(el => el.remove());
  }

  getActivePage() {
    return this.pageWrapper.querySelector('.paymentCard-paginationListItem.active');
  }

  getActivePageNumber() {
    const activePage = this.getActivePage();
    return activePage ? parseInt(activePage.getAttribute('data-page'), 10) : null;
  }

  updateButtonStatus(activePage) {
    if (activePage === 1) {
      /** Left */
      addClass(this.previousPageButton, 'disabled');
      addClass(this.firstPage, 'disabled');
      this.firstPage.setAttribute('disabled', 'disabled');
      this.previousPageButton.setAttribute('disabled', 'disabled');
      /** Right */
      removeClass(this.nextPageButton, 'disabled');
      removeClass(this.lastPage, 'disabled');
      this.nextPageButton.removeAttribute('disabled');
      this.lastPage.removeAttribute('disabled');
    } else if (activePage === this.totalPage) {
      /** Right */
      addClass(this.nextPageButton, 'disabled');
      addClass(this.lastPage, 'disabled');
      this.nextPageButton.setAttribute('disabled', 'disabled');
      this.lastPage.setAttribute('disabled', 'disabled');
      /** Left */
      removeClass(this.previousPageButton, 'disabled');
      removeClass(this.firstPage, 'disabled');
      this.previousPageButton.removeAttribute('disabled');
      this.firstPage.removeAttribute('disabled');
    } else {
      /** Left */
      removeClass(this.previousPageButton, 'disabled');
      removeClass(this.firstPage, 'disabled');
      this.previousPageButton.removeAttribute('disabled');
      this.firstPage.removeAttribute('disabled');
      /** Right */
      removeClass(this.nextPageButton, 'disabled');
      removeClass(this.lastPage, 'disabled');
      this.nextPageButton.removeAttribute('disabled');
      this.lastPage.removeAttribute('disabled');
    }
  }

  bindPrevButtonClick() {
    this.previousPageButton.removeEventListener('click', this.handlePrevClick);
    this.previousPageButton.addEventListener('click', this.handlePrevClick);
    this.firstPage.removeEventListener('click', this.handleFirstClick);
    this.firstPage.addEventListener('click', this.handleFirstClick);
  }

  bindNextButtonClick() {
    this.nextPageButton.removeEventListener('click', this.handleNextClick);
    this.nextPageButton.addEventListener('click', this.handleNextClick);
    this.lastPage.removeEventListener('click', this.handleLastClick);
    this.lastPage.addEventListener('click', this.handleLastClick);
  }

  handlePrevClick() {
    const activePageNumber = this.getActivePageNumber();
    if (activePageNumber) {
      this.selectedPage = activePageNumber - 1;
      this.handlePageChange(activePageNumber - 1);
    };
  }

  handleFirstClick() {
    this.selectedPage = 1;
    this.handlePageChange(1);
  }

  handleLastClick() {
    this.selectedPage = this.totalPage;
    this.handlePageChange(this.totalPage);
  }

  handleNextClick() {
    const activePageNumber = this.getActivePageNumber();
    if (activePageNumber) {
      this.selectedPage = activePageNumber + 1;
      this.handlePageChange(activePageNumber + 1);
    }
  }

  handlePageChange(nextPageNumber) {
    if (nextPageNumber > this.visibleLastPage || nextPageNumber < this.visibleFirstPage) {
      this.createPaginationItems();
    }

    this.changeActivePage(this.getActivePage(), nextPageNumber);

    this.updateProductList(nextPageNumber);

    this.updateButtonStatus(nextPageNumber);

    this.triggerPageChange();

    this.setNextPageProductsHeight();
  }

  updateProductList(activePage) {
    this.products.forEach((p, index) => {
      const product = p;
      product.style.display = index < (activePage - 1) * this.productsPerPage || (index + 1) > (activePage * this.productsPerPage) ? 'none' : 'block';
    });
  }

  changeActivePage(activePage, nextPage) {
    const nextPageItem = this.qLine.querySelector(`.paymentCard-paginationListItem[data-page="${(nextPage)}"]`);
    removeClass(activePage, 'active');
    if (nextPageItem) {
      addClass(nextPageItem, 'active');
    }
  }

  getProductsPerPage() {
    const isMobile = document.querySelector('.isMobile');
    if (isMobile) return this.products.length;

    const paginationWrapperDisplayStyle = global.getComputedStyle(this.paginationWrapper).display;
    this.paginationWrapper.style.display = 'block';

    const availableSpaceForListItem = this.getAvaibleMaxHeight(this.productsListWrapper, '.jfCard-question');

    this.paginationWrapper.style.display = paginationWrapperDisplayStyle;

    // get the tallest product's height
    const maxProductHeight = Array.from(this.products).reduce((max, product) => {
      const height = parseInt(global.getComputedStyle(product).height, 10);
      return height > max ? height : max;
    }, 0);

    const productsPerPage = parseInt(availableSpaceForListItem / maxProductHeight, 10) || 1;
    return parseInt(productsPerPage, 10) > 0 ? productsPerPage : 1;
  }

  // fixes the number of products per page and products list wrappers height accordingly
  setProductsPerPage() {
    // In src/helpers/payment/PaymentContent.js, we render 'product-summary' if paymentType === 'product' but sometimes it can be equal to 'subscription' so we do not render.
    // we need to check if we have productsSummaryWrapper !== undefined so I added this check
    if ((this.productsSummaryWrapper && this.productsSummaryWrapper.clientHeight !== 0) || hasClass(this.productsListWrapper, 'products--selected')) {
      return;
    }
    const isMobile = document.querySelector('.isMobile');
    if (isMobile) return this.products.length;

    this.setNextPageProductsHeight();
    const availableSpaceForListItem = this.getAvaibleMaxHeight(this.productsListWrapper, '.jfCard-question');

    const maxProductHeight = Array.from(this.products).reduce((max, product) => {
      const height = parseInt(global.getComputedStyle(product).height, 10);
      return height > max ? height : max;
    }, 0);

    const productsPerPage = parseInt(availableSpaceForListItem / maxProductHeight, 10) || 1;
    this.productsPerPageFixed = parseInt(productsPerPage, 10) > 0 ? productsPerPage : 1;

    this.initProductPages();
  }
  
  // sets next page products list wrapper height
  setNextPageProductsHeight() {
    // const productsArr = Array.from(this.productsListWrapper.children);
    // const elementFixedHeight = productsArr.reduce((result, current) => result + current.clientHeight, 0); // that calculation blocks multiple product displaying in cardform
    this.productsListWrapper.style.height = 'auto'; // we set the height auto to fix displaying multiple products
  }

  getAvaibleMaxHeight(element, stopSelector) {
    if ((stopSelector && element.matches(stopSelector)) || !element.parentElement) return this.getStopNodeInnerHeight(element);
    const elementSiblings = this.getElementSiblings(element);
    const siblingsHeight = elementSiblings.reduce((acc, curr) => acc + this.getSiblingHeight(curr), 0);
    return this.getAvaibleMaxHeight(element.parentElement, stopSelector) - (this.getNodeMarginAndPadding(element) + siblingsHeight);
  }

  getElementSiblings(element) {
    const filterSiblings = el => node => node !== el;
    return Array.from(element.parentElement.childNodes).filter(filterSiblings(element));
  }

  getSiblingHeight(element) {
    return this.getStyleSum(element, ['height', 'marginTop', 'marginBottom']);
  }

  getNodeMarginAndPadding(element) {
    return this.getStyleSum(element, ['marginTop', 'marginBottom', 'paddingTop', 'paddingBottom']);
  }

  getStopNodeInnerHeight(element) {
    return this.getStyleSum(element, ['maxHeight']) - this.getStyleSum(element, ['paddingTop', 'paddingBottom']);
  }

  getStyleSum(element, styles) {
    const computedStyle = global.getComputedStyle(element);
    return styles.reduce((sum, style) => sum + (parseInt(computedStyle[style], 10) || 0), 0);
  }

  setOnPageChange(fn) {
    this.onPageChange = fn;
  }

  triggerPageChange() {
    if (this.onPageChange) this.onPageChange();
  }
}
